import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { authBaseUrl, baseUrl } from '../../constants/appConstants';
import {
  emptyLocalStorage,
  getRefreshTokenFromLocalStorage,
  getTokenFromLocalStorage,
  setRefreshTokenLocalStorage,
  setTokenLocalStorage,
} from '../../utils/helper';
import { errorMsg } from '../../constants/msgs';

// Function to check internet connectivity
const checkConnectivity = () => {
  return navigator.onLine;
};

const baseQuery = fetchBaseQuery({
  baseUrl: baseUrl,
  credentials: 'same-origin',
  prepareHeaders: async (headers) => {
    if (checkConnectivity()) {
      const token = getTokenFromLocalStorage();
      // Bearer works for BOTH the Synapse backend (verifies EdDSA via JWKS) and
      // the central auth server (requireAuth). Auth routes use absolute URLs so
      // they bypass `baseUrl` but still pick up this header.
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      headers.set('Accept', '*/*');
    } else {
      throw new Error('No internet connection');
    }
    return headers;
  },
});

// Rotate the central-auth refresh token (single-flight). Uses bare fetch so it
// never recurses through this base query. Auth returns { access, refresh }.
let refreshPromise = null;
const refreshAccessToken = () => {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      const refresh = getRefreshTokenFromLocalStorage();
      if (!refresh || !authBaseUrl) return null;
      try {
        const res = await fetch(`${authBaseUrl}/v1/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh, app: 'synapse' }),
        });
        if (!res.ok) return null;
        const json = await res.json().catch(() => ({}));
        const data = json?.data ?? {};
        if (!data.access) return null;
        setTokenLocalStorage(data.access);
        if (data.refresh) setRefreshTokenLocalStorage(data.refresh);
        return data.access;
      } catch {
        return null;
      }
    })().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
};

// Pre-auth endpoints: a 401 here means "bad credentials / expired challenge",
// NOT "access token expired" — never refresh or reload; let the page show the
// error (a failed login must not trigger a page reload).
const PUBLIC_AUTH_ENDPOINTS = ['signin', 'forgotPassword', 'verifyOtp', 'resetPassword', 'signout'];

const baseQueryWithReauth = async (args, api, extraOptions) => {
  try {
    let result = await baseQuery(args, api, extraOptions);

    if (
      result.error &&
      result.error.status === 401 &&
      !PUBLIC_AUTH_ENDPOINTS.includes(api.endpoint)
    ) {
      // Short-lived EdDSA access token expired → rotate once and retry, instead
      // of the old hard logout-on-401. Only log out if the refresh itself fails.
      const newToken = await refreshAccessToken();
      if (newToken) {
        result = await baseQuery(args, api, extraOptions);
      } else {
        emptyLocalStorage();
        setTimeout(() => {
          window.location.reload();
        }, 1200);
      }
    }
    return result;
  } catch (error) {
    if (error.message === 'No internet connection') {
      errorMsg('Please check your internet connection');
    }
    throw error;
  }
};

export const apiSlice = createApi({
  baseQuery: baseQueryWithReauth,
  tagTypes: ['SUPPORT_REQUESTS'],
  endpoints: (_builder) => ({}),
});
